/**
 * Barra de pestañas. Va **entre el buscador y el contenido**, siempre.
 *
 * ── Por qué existe, y por qué reemplaza a un filtro con valor por omisión ──
 * Clientes y Cuestionarios arrancaban con el filtro «activo = sí» ya aplicado
 * dentro del panel de filtros. Funcionaba, y estaba mal: **la lista escondía
 * cosas y la razón estaba a dos clics de distancia**, plegada dentro del
 * buscador. Quien no encontraba un cliente desactivado no tenía forma de sospechar
 * por qué — el filtro era invisible hasta que se abría el panel.
 *
 * Una pestaña dice **en la pantalla** qué subconjunto se está viendo y cuáles son
 * los otros. Sigue arrancando en «Activos», que es lo que se quiere el 95 % de
 * las veces, pero ahora eso es una elección visible y no un valor escondido.
 *
 * ⚠️ **El buscador manda sobre la pestaña, no al revés.** Por eso van debajo: lo
 * que se escribe arriba se aplica dentro de la pestaña elegida.
 */
export type Pestana<C extends string> = {
  clave: C;
  texto: string;
  /** Contador al lado del texto. Solo se pinta si es mayor que cero. */
  n?: number | undefined;
};

export function Pestanas<C extends string>({
  pestanas,
  activa,
  alElegir,
  etiqueta,
}: {
  pestanas: readonly Pestana<C>[];
  activa: C;
  alElegir: (clave: C) => void;
  /** Para el lector de pantalla: «Estado», «Tipo de usuario». */
  etiqueta: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={etiqueta}
      className="mb-4 flex flex-wrap gap-2 border-b border-marino-100"
    >
      {pestanas.map((p) => {
        const seleccionada = p.clave === activa;
        return (
          <button
            key={p.clave}
            type="button"
            role="tab"
            aria-selected={seleccionada}
            onClick={() => alElegir(p.clave)}
            className={`-mb-px flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm font-semibold
              transition focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15
              ${
                seleccionada
                  ? 'border-naranja-500 text-marino-800'
                  : 'border-transparent text-marino-400 hover:text-marino-700'
              }`}
          >
            {p.texto}
            {p.n !== undefined && p.n > 0 && (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-naranja-500 px-1.5 text-xs font-bold text-marino-900">
                {p.n}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Las tres de estado, que son iguales en todas las pantallas. */
export const PESTANAS_ESTADO = [
  { clave: 'si', texto: 'Activos' },
  { clave: 'no', texto: 'Desactivados' },
  { clave: 'todas', texto: 'Todos' },
] as const satisfies readonly Pestana<'si' | 'no' | 'todas'>[];

export type EstadoVisible = (typeof PESTANAS_ESTADO)[number]['clave'];
